import type { PageMeta } from './page-meta';
import { absoluteAsset, absoluteUrl, SITE_CONFIG } from './site-config';

const ORG_ID = `${SITE_CONFIG.url}/#organization`;
const WEBSITE_ID = `${SITE_CONFIG.url}/#website`;

export function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    logo: absoluteAsset('/favicon.svg'),
    email: SITE_CONFIG.email,
    telephone: SITE_CONFIG.phone,
    foundingDate: String(SITE_CONFIG.foundingYear),
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE_CONFIG.address.street,
      addressLocality: SITE_CONFIG.address.city,
      addressCountry: SITE_CONFIG.address.countryCode,
    },
    sameAs: [SITE_CONFIG.linkedIn],
  };
}

export function buildWebSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    description:
      'Software Lab designs, builds, and ships custom software and B2B SaaS products from Sofia, Bulgaria.',
    publisher: { '@id': ORG_ID },
    inLanguage: SITE_CONFIG.language,
  };
}

function buildBreadcrumbJsonLd(meta: PageMeta, pageUrl: string) {
  if (!meta.breadcrumb?.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: meta.breadcrumb.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.path.includes('#') ? `${SITE_CONFIG.url}${item.path}` : absoluteUrl(item.path),
    })),
  };
}

function buildWebPageJsonLd(meta: PageMeta, pageUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: meta.title,
    description: meta.description,
    isPartOf: { '@id': WEBSITE_ID },
    about: meta.path === '/' ? { '@id': ORG_ID } : undefined,
    inLanguage: SITE_CONFIG.language,
  };
}

function buildSoftwareApplicationJsonLd(meta: PageMeta, pageUrl: string) {
  if (!meta.product) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: meta.product.name,
    applicationCategory: meta.product.applicationCategory,
    operatingSystem: meta.product.operatingSystem,
    url: meta.product.url,
    description: meta.description,
    provider: { '@id': ORG_ID },
  };
}

export function buildPageJsonLd(meta: PageMeta, pageUrl: string) {
  const graph = [
    buildOrganizationJsonLd(),
    buildWebSiteJsonLd(),
    buildWebPageJsonLd(meta, pageUrl),
    buildBreadcrumbJsonLd(meta, pageUrl),
    buildSoftwareApplicationJsonLd(meta, pageUrl),
  ].filter(Boolean);

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
